import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Sparkles, Bell, ArrowLeft, Loader2, Crown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api/axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_interpret-now/artifacts/8sijz41w_PassportTalk.png";

const PricingPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [isVipUser, setIsVipUser] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isInternalTester, setIsInternalTester] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [subscriptionExpires, setSubscriptionExpires] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("monthly"); // "monthly" or "annual"
  
  // VIP Pricing
  const VIP_PRICING = {
    monthly: { price: 9.99, display: "$9.99/month", savings: null },
    annual: { price: 79.99, display: "$79.99/year", savings: "Save $39.89/year" }
  };
  
  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      // Poll for payment status
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled. You can try again anytime.');
    }
  }, [searchParams]);

  // Check subscription status on mount
  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await api.get('/subscription/me');
      setIsVipUser(response.data.is_vip);
      setIsAdminUser(response.data.is_admin || false);
      setIsInternalTester(response.data.is_internal_tester || false);
      setSubscriptionPlan(response.data.subscription_plan);
      setSubscriptionExpires(response.data.expires_at);
    } catch (error) {
      console.log('Subscription check failed:', error.message);
    }
  };

  // Poll payment status after Stripe redirect
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      setCheckingStatus(false);
      return;
    }

    setCheckingStatus(true);

    try {
      const response = await api.get(`/subscription/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setIsVipUser(true);
        setCheckingStatus(false);
        toast.success('🎉 Welcome to VIP! Enjoy all premium features.');
        // Clear URL params
        window.history.replaceState({}, '', '/pricing');
        // Refresh subscription status
        checkSubscriptionStatus();
        return;
      } else if (response.data.status === 'expired') {
        setCheckingStatus(false);
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingStatus(false);
      toast.error('Error checking payment status. Please refresh the page.');
    }
  }, []);

  const handleUpgrade = async (plan) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to upgrade');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await api.post('/subscription/checkout', {
        plan: plan,
        origin_url: originUrl
      });

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error("Please enter a valid email");
      return;
    }
    
    const existingEmails = JSON.parse(localStorage.getItem('passportTalk_vipWaitlist') || '[]');
    if (!existingEmails.includes(email)) {
      existingEmails.push(email);
      localStorage.setItem('passportTalk_vipWaitlist', JSON.stringify(existingEmails));
    }
    
    setSubmitted(true);
    toast.success("You're on the list! We'll notify you about VIP offers.");
  };

  const freeFeatures = [
    { text: "Unlimited translations", included: true },
    { text: "5+ Spanish dialects", included: true },
    { text: "Gender-aware grammar", included: true },
    { text: "WhatsApp copy & share", included: true },
    { text: "Save favorites", included: true },
    { text: "Occasional ads", included: true, note: "~every 10 translations" },
  ];

  const vipFeatures = [
    { text: "Everything in Free", included: true },
    { text: "Ad-free experience", included: true, highlight: true },
    { text: "Live agent access (June 2026)", included: true, highlight: true },
    { text: "Priority support", included: true },
    { text: "Premium dialect-aware translations", included: true },
    { text: "Early access to new features", included: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="PassportTalk" className="w-6 h-6 object-contain" />
            <span className="font-bold text-slate-800 font-outfit">PassportTalk</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Payment Status Check */}
        {checkingStatus && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 text-center max-w-sm mx-4">
              <Loader2 className="w-12 h-12 text-[#0EA5E9] animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Processing Payment</h3>
              <p className="text-slate-600">Please wait while we confirm your payment...</p>
            </div>
          </div>
        )}

        {/* VIP/Admin/Internal Tester User Banner */}
        {isVipUser && (
          <div className={`rounded-2xl p-6 mb-8 text-white ${
            isAdminUser 
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600'
              : isInternalTester
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                : 'bg-gradient-to-r from-amber-500 to-orange-500'
          }`} data-testid="vip-user-banner">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Crown className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  {isAdminUser 
                    ? 'Admin Account' 
                    : isInternalTester 
                      ? 'Internal Tester Account'
                      : "You're a VIP Member!"}
                  <CheckCircle2 className="w-5 h-5" />
                </h3>
                <p className="text-white/90 text-sm">
                  {isAdminUser 
                    ? 'Full access enabled. Ads disabled. All features unlocked.'
                    : isInternalTester
                      ? 'Testing mode active. Full VIP access. No ads. No payment required.'
                      : 'Enjoy all premium features including live agent access.'
                  }
                  {!isAdminUser && !isInternalTester && subscriptionPlan && (
                    <span className="ml-2 capitalize">Plan: {subscriptionPlan}</span>
                  )}
                  {!isAdminUser && !isInternalTester && subscriptionExpires && (
                    <span className="ml-2">
                      • Renews: {new Date(subscriptionExpires).toLocaleDateString()}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="text-center mb-10">
          <span className="px-3 py-1 bg-amber-500/10 text-amber-600 text-xs font-semibold rounded-full mb-4 inline-block">
            {isAdminUser ? 'Admin' : isInternalTester ? 'Tester' : (isVipUser ? 'VIP Member' : 'Go VIP')}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-outfit mb-4">
            {isAdminUser 
              ? 'Admin Access Enabled' 
              : isInternalTester 
                ? 'Internal Tester Access'
                : (isVipUser ? 'Thank You for Being VIP!' : 'Unlock the Full Experience')}
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            {isAdminUser
              ? 'You have full admin access. All restrictions are bypassed.'
              : isInternalTester
                ? 'You have full customer VIP experience for testing. No ads. No payment required.'
                : (isVipUser 
                  ? 'Your VIP membership is active. Enjoy all premium features!'
                  : 'Get live agent access, ad-free experience, and premium translations.')
            }
          </p>
        </div>

        {/* Plan Toggle (Monthly/Annual) - Only show for non-VIP users */}
        {!isVipUser && (
          <div className="flex justify-center mb-8">
            <div className="bg-slate-100 rounded-xl p-1 inline-flex" data-testid="plan-toggle">
              <button
                onClick={() => setSelectedPlan("monthly")}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedPlan === "monthly"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="plan-toggle-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setSelectedPlan("annual")}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedPlan === "annual"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="plan-toggle-annual"
              >
                Annual
                <span className="ml-2 text-xs text-emerald-600 font-semibold">Save 33%</span>
              </button>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Free Plan */}
          <div className={`bg-white rounded-2xl border-2 p-6 sm:p-8 ${isVipUser ? 'border-slate-200 opacity-75' : 'border-slate-200'}`}>
            <div className="mb-6">
              <h3 className="text-xl font-bold text-slate-900 mb-1">Free</h3>
              <p className="text-slate-500 text-sm">Great for getting started</p>
            </div>
            
            <div className="mb-6">
              <span className="text-4xl font-bold text-slate-900">$0</span>
              <span className="text-slate-500">/forever</span>
            </div>

            <div className="mb-6">
              <Link
                to="/conversation"
                className={`block w-full py-3 px-4 font-semibold rounded-xl text-center transition-all ${
                  isVipUser 
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {isVipUser ? 'Previous Plan' : 'Current Plan'}
              </Link>
            </div>

            <ul className="space-y-3">
              {freeFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {feature.included ? (
                      <Check className="w-3 h-3 text-slate-600" />
                    ) : (
                      <X className="w-3 h-3 text-slate-400" />
                    )}
                  </div>
                  <span className={`text-sm ${feature.included ? 'text-slate-700' : 'text-slate-400'}`}>
                    {feature.text}
                    {feature.note && (
                      <span className="text-slate-400 text-xs ml-1">({feature.note})</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* VIP Plan */}
          <div className={`rounded-2xl border-2 p-6 sm:p-8 relative ${
            isVipUser 
              ? 'bg-gradient-to-b from-amber-500/10 to-white border-amber-500'
              : 'bg-gradient-to-b from-amber-500/5 to-white border-amber-500'
          }`}>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                <Crown className="w-3 h-3" />
                {isVipUser ? 'Your Plan' : 'VIP'}
              </span>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold text-slate-900 mb-1 flex items-center gap-2">
                VIP
                <Sparkles className="w-5 h-5 text-amber-500" />
              </h3>
              <p className="text-slate-500 text-sm">Full PassportTalk experience</p>
            </div>
            
            <div className="mb-6">
              <span className="text-4xl font-bold text-slate-900">
                ${selectedPlan === "annual" ? "79.99" : "9.99"}
              </span>
              <span className="text-slate-500">/{selectedPlan === "annual" ? "year" : "month"}</span>
              {selectedPlan === "annual" && (
                <p className="text-xs text-emerald-600 font-medium mt-1">
                  {VIP_PRICING.annual.savings}
                </p>
              )}
            </div>

            <div className="mb-6">
              {isVipUser ? (
                <button
                  disabled
                  className="block w-full py-3 px-4 bg-amber-500/10 text-amber-600 font-semibold rounded-xl text-center"
                  data-testid="vip-active-button"
                >
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Active
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => handleUpgrade(selectedPlan)}
                  disabled={loading}
                  className="block w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl text-center hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50"
                  data-testid="upgrade-button"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Starting checkout...
                    </span>
                  ) : (
                    `Go VIP - ${VIP_PRICING[selectedPlan].display}`
                  )}
                </button>
              )}
            </div>

            <ul className="space-y-3">
              {vipFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${feature.highlight ? 'bg-amber-500' : 'bg-amber-500/10'}`}>
                    <Check className={`w-3 h-3 ${feature.highlight ? 'text-white' : 'text-amber-600'}`} />
                  </div>
                  <span className={`text-sm ${feature.highlight ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* VIP Benefits Highlight */}
        {!isVipUser && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 mb-8 border border-amber-200">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              VIP Benefits
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🎯</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Live Agent Access <span className="text-xs text-emerald-600">(June 2026)</span></p>
                  <p className="text-sm text-slate-600">Book 1-on-1 sessions with language experts</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🚫</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Ad-Free Experience</p>
                  <p className="text-sm text-slate-600">No interruptions, just translations</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">⚡</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Priority Support</p>
                  <p className="text-sm text-slate-600">Get help faster when you need it</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🌍</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Premium Translations</p>
                  <p className="text-sm text-slate-600">Dialect-aware, culturally accurate</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Capture (only for non-VIP users) */}
        {!isVipUser && (
          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 text-center">
            <Bell className="w-10 h-10 text-amber-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Not Ready Yet?
            </h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Get notified about special VIP offers and new features.
            </p>

            {!submitted ? (
              <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all"
                  data-testid="pricing-email-input"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-all whitespace-nowrap"
                  data-testid="pricing-email-submit"
                >
                  Notify Me
                </button>
              </form>
            ) : (
              <div className="flex items-center justify-center gap-2 text-emerald-600 font-medium">
                <Check className="w-5 h-5" />
                <span>You're on the list!</span>
              </div>
            )}
          </div>
        )}

        {/* Live Agents Coming Soon Notice */}
        <div className="mt-8 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <p className="text-sm text-emerald-800 text-center">
            <strong>Live Agent Access:</strong> Coming June 2026. VIP members will get priority access to 1-on-1 sessions with bilingual experts.
          </p>
        </div>

        {/* FAQ or Note */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Questions? <Link to="/beta" className="text-amber-600 hover:underline">Learn more about Early Access</Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-center gap-4 text-sm text-slate-500">
          <Link to="/terms" className="hover:text-slate-700">Terms</Link>
          <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          <Link to="/refunds" className="hover:text-slate-700">Refunds</Link>
          <Link to="/beta" className="hover:text-slate-700">Early Access</Link>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
